import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export interface PostAuthor {
  id: number;
  firstName: string;
  middleName?: string;
  lastName: string;
  profileImageUrl?: string;
  username: string;
}

export interface PostMedia {
  id: number;
  mediaType: string;
  mediaUrl?: string;
  fileName?: string;
  contentType?: string;
  displayOrder: number;
}

export interface PostComment {
  id: number;
  author: PostAuthor;
  content: string;
  createdAt: string;
  updatedAt?: string;
}

export interface Post {
  id: number;
  author: PostAuthor;
  textContent?: string;
  media: PostMedia[];
  likeCount: number;
  commentCount: number;
  isLikedByCurrentUser: boolean;
  createdAt: string;
  updatedAt?: string;
  comments?: PostComment[];
}

export interface PostsResponse {
  posts: Post[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
  hasMore: boolean;
}

export interface CreatePostData {
  textContent?: string;
  mediaFiles?: File[];
}

@Injectable({
  providedIn: 'root'
})
export class PostService {
  private apiUrl = `${environment.apiUrl}/api/post`;

  constructor(private http: HttpClient) { }

  createPost(data: CreatePostData): Observable<any> {
    const formData = new FormData();
    
    if (data.textContent) {
      formData.append('textContent', data.textContent);
    }
    
    if (data.mediaFiles && data.mediaFiles.length > 0) {
      data.mediaFiles.forEach((file, index) => {
        formData.append('mediaFiles', file);
      });
    }

    return this.http.post<any>(`${this.apiUrl}`, formData);
  }

  getFeed(page: number = 1, pageSize: number = 10): Observable<any> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('pageSize', pageSize.toString());
    
    return this.http.get<any>(`${this.apiUrl}/feed`, { params });
  }

  getPost(id: number): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/${id}`);
  }

  deletePost(id: number): Observable<any> {
    return this.http.delete<any>(`${this.apiUrl}/${id}`);
  }

  toggleLike(postId: number): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/${postId}/like`, {});
  }

  addComment(postId: number, content: string): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/${postId}/comments`, { content });
  }

  updateComment(commentId: number, content: string): Observable<any> {
    return this.http.put<any>(`${this.apiUrl}/comments/${commentId}`, { content });
  }

  deleteComment(commentId: number): Observable<any> {
    return this.http.delete<any>(`${this.apiUrl}/comments/${commentId}`);
  }

  getComments(postId: number, skip: number = 0, take: number = 10): Observable<any> {
    const params = new HttpParams()
      .set('skip', skip.toString())
      .set('take', take.toString());
    
    return this.http.get<any>(`${this.apiUrl}/${postId}/comments`, { params });
  }
}

